import { Bot, Lightbulb, PhoneOff, User, VolumeX, Wrench } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Card } from '@apps/web/components/ui/card';
import {
    classifyEntry,
    coalesceTimelineEntries,
    countEntriesByLane,
    type DeveloperTimelineEntry,
    extractTtsPreview,
    formatOffsetSeconds,
    formatSlug,
    formatTtsEventKind,
    getEntryOffsetSeconds,
    getTimelineBaselineMs,
    parseAdvisorPayload,
    type TimelineLane,
} from '@apps/web/components/voice_calls/developer_timeline_helpers';
import {
    getDisconnectAttributionLabel,
    getDisconnectAttributionSourceLabel,
    isPhoebeDisconnectAttribution,
    type VoiceCallDisconnectAttributionLike,
} from '@apps/web/components/voice_calls/voice_call_disconnect_attribution';
import { cn } from '@apps/web/lib/utils';

export type { DeveloperTimelineEntry } from '@apps/web/components/voice_calls/developer_timeline_helpers';

type DeveloperTranscriptTimelineProps = {
    readonly entries: ReadonlyArray<DeveloperTimelineEntry>;
    readonly disconnectAttribution?: VoiceCallDisconnectAttributionLike | null;
    readonly className?: string;
};

type LaneToggleKey = Exclude<TimelineLane, 'other'>;

type LaneConfig = {
    readonly key: LaneToggleKey;
    readonly label: string;
    readonly defaultOn: boolean;
};

const LANE_TOGGLES: ReadonlyArray<LaneConfig> = [
    { key: 'conversation', label: 'Conversation', defaultOn: true },
    { key: 'tool', label: 'Tools', defaultOn: true },
    { key: 'advisor', label: 'Advisor', defaultOn: true },
    { key: 'tts_event', label: 'TTS events', defaultOn: false },
];

function renderJsonBlock(
    label: string,
    value: Record<string, unknown> | null | undefined,
) {
    if (!value || Object.keys(value).length === 0) {
        return null;
    }
    return (
        <div className="min-w-0 space-y-1">
            <p className="text-muted-foreground text-xs font-medium">{label}</p>
            <pre className="max-w-full min-w-0 rounded-md bg-slate-950/5 p-1.5 font-mono text-xs leading-snug [overflow-wrap:anywhere] break-words whitespace-pre-wrap text-slate-900 dark:bg-slate-50/5 dark:text-slate-100">
                {JSON.stringify(value, null, 2)}
            </pre>
        </div>
    );
}

function renderToolEntry(entry: DeveloperTimelineEntry) {
    const headerLabel =
        entry.kind === 'tool_result'
            ? 'Tool Result'
            : entry.kind === 'tool_start'
              ? 'Tool Invocation'
              : 'Tool Batch';

    const plannedCalls: ReadonlyArray<Record<string, unknown>> | null =
        Array.isArray(entry.function_calls)
            ? entry.function_calls
            : Array.isArray(entry.metadata?.['function_calls'])
              ? (entry.metadata['function_calls'] as ReadonlyArray<
                    Record<string, unknown>
                >)
              : null;

    return (
        <Card className="min-w-0 border border-amber-200 bg-amber-50/70 p-2 shadow-none dark:border-amber-900/60 dark:bg-amber-950/30">
            <div className="flex flex-wrap items-center gap-1.5">
                <Wrench className="h-3.5 w-3.5 text-amber-700 dark:text-amber-200" />
                <p className="text-xs font-semibold tracking-wide text-amber-800 uppercase dark:text-amber-100">
                    {headerLabel}
                </p>
                {entry.function_name ? (
                    <code className="min-w-0 rounded bg-amber-100/80 px-1.5 py-0.5 font-mono text-xs [overflow-wrap:anywhere] text-amber-900 dark:bg-amber-900/60 dark:text-amber-100">
                        {entry.function_name}
                    </code>
                ) : null}
                <Badge
                    variant="outline"
                    className="h-6 px-2 text-xs leading-none uppercase"
                >
                    {entry.kind.replace('tool_', '').replace('_', ' ')}
                </Badge>
            </div>
            {entry.tool_call_id ? (
                <p className="text-muted-foreground mt-1 min-w-0 font-mono text-xs [overflow-wrap:anywhere]">
                    {entry.tool_call_id}
                </p>
            ) : null}
            <div className="mt-1.5 space-y-1.5">
                {entry.kind === 'tool_batch' && plannedCalls
                    ? renderJsonBlock('Planned calls', {
                          calls: plannedCalls,
                      } as Record<string, unknown>)
                    : null}
                {renderJsonBlock('Arguments', entry.arguments)}
                {renderJsonBlock('Result', entry.result)}
                {renderJsonBlock('Metadata', entry.metadata)}
            </div>
        </Card>
    );
}

function renderConversationEntry(entry: DeveloperTimelineEntry) {
    const isAssistant =
        entry.role === 'assistant' ||
        entry.kind === 'assistant_text' ||
        entry.kind === 'bot_started_speaking';
    const SpeakerIcon = isAssistant ? Bot : User;
    return (
        <Card
            className={cn(
                'min-w-0 p-2 shadow-none',
                isAssistant
                    ? 'border border-violet-200 bg-violet-50/70 dark:border-violet-900/60 dark:bg-violet-950/30'
                    : 'border border-sky-200 bg-sky-50/70 dark:border-sky-900/60 dark:bg-sky-950/30',
            )}
        >
            <div className="flex items-center gap-1.5">
                <SpeakerIcon
                    className={cn(
                        'h-3.5 w-3.5',
                        isAssistant
                            ? 'text-violet-700 dark:text-violet-200'
                            : 'text-sky-700 dark:text-sky-200',
                    )}
                />
                <p
                    className={cn(
                        'text-xs font-semibold tracking-wide uppercase',
                        isAssistant
                            ? 'text-violet-700 dark:text-violet-200'
                            : 'text-sky-700 dark:text-sky-200',
                    )}
                >
                    {isAssistant ? 'Agent' : 'Caregiver'}
                </p>
            </div>
            <p className="mt-1 min-w-0 text-sm leading-snug [overflow-wrap:anywhere] text-slate-900 dark:text-slate-50">
                {entry.text}
            </p>
        </Card>
    );
}

function renderAdvisorEntry(entry: DeveloperTimelineEntry) {
    const advice = parseAdvisorPayload(entry);
    return (
        <Card className="min-w-0 border border-emerald-300 bg-emerald-50/70 p-2 shadow-none dark:border-emerald-800/60 dark:bg-emerald-950/30">
            <div className="flex flex-wrap items-center gap-1.5">
                <Lightbulb className="h-3.5 w-3.5 text-emerald-700 dark:text-emerald-200" />
                <p className="text-xs font-semibold tracking-wide text-emerald-800 uppercase dark:text-emerald-100">
                    Advisor
                </p>
                {advice.adviceType ? (
                    <Badge
                        variant="outline"
                        className="h-6 border-emerald-300 px-2 text-xs leading-none text-emerald-800 uppercase dark:border-emerald-700 dark:text-emerald-100"
                    >
                        {formatSlug(advice.adviceType)}
                    </Badge>
                ) : null}
                {advice.bucket ? (
                    <Badge
                        variant="secondary"
                        className="h-6 px-2 text-xs leading-none"
                    >
                        {formatSlug(advice.bucket)}
                    </Badge>
                ) : null}
                {advice.secondaryBucket ? (
                    <Badge
                        variant="outline"
                        className="h-6 px-2 text-xs leading-none"
                    >
                        {formatSlug(advice.secondaryBucket)}
                    </Badge>
                ) : null}
            </div>
            {advice.reason ? (
                <p className="mt-1 min-w-0 text-sm leading-snug [overflow-wrap:anywhere] text-slate-900 dark:text-slate-50">
                    {advice.reason}
                </p>
            ) : null}
            {advice.evidence.length > 0 ? (
                <ul className="mt-1.5 space-y-1">
                    {advice.evidence.slice(0, 3).map((item, index) => (
                        <li
                            key={index}
                            className="text-muted-foreground min-w-0 text-xs [overflow-wrap:anywhere] italic"
                        >
                            &ldquo;{item}&rdquo;
                        </li>
                    ))}
                </ul>
            ) : null}
            {advice.suggestedActionType ? (
                <p className="text-muted-foreground mt-2 text-xs">
                    Suggested action:{' '}
                    <code className="rounded bg-emerald-100/80 px-1 py-0.5 font-mono text-xs [overflow-wrap:anywhere] text-emerald-900 dark:bg-emerald-900/60 dark:text-emerald-100">
                        {advice.suggestedActionType}
                    </code>
                </p>
            ) : null}
        </Card>
    );
}

function renderTtsEventEntry(entry: DeveloperTimelineEntry, count: number) {
    const preview = extractTtsPreview(entry);
    return (
        <div className="flex items-center gap-2 py-1">
            <VolumeX className="text-muted-foreground h-3 w-3" />
            <span className="text-muted-foreground text-xs italic">
                {formatTtsEventKind(entry.kind)}
                {count > 1 ? ` × ${count}` : null}
                {preview ? `: "${preview}"` : null}
            </span>
        </div>
    );
}

function renderDisconnectAttributionEntry(
    attribution: VoiceCallDisconnectAttributionLike,
) {
    const sourceLabel = getDisconnectAttributionSourceLabel(attribution);
    const label =
        getDisconnectAttributionLabel(attribution) ??
        (attribution.details_available === false
            ? (sourceLabel ?? 'Disconnect attribution unavailable')
            : null);
    if (!label) {
        return null;
    }

    const sourceBadgeLabel = sourceLabel !== label ? sourceLabel : null;
    const isPhoebeDisconnect = isPhoebeDisconnectAttribution(attribution);

    return (
        <div className="grid grid-cols-[3rem_1fr_3rem] items-start gap-2 text-sm">
            <span className="text-muted-foreground pt-2 font-mono text-[10px]">
                end
            </span>
            <Card
                className={cn(
                    'p-3 shadow-none',
                    isPhoebeDisconnect
                        ? 'border border-red-200 bg-red-50/70 dark:border-red-900/60 dark:bg-red-950/30'
                        : 'border border-slate-200 bg-slate-50/80 dark:border-slate-800/70 dark:bg-slate-950/30',
                )}
            >
                <div className="flex flex-wrap items-center gap-2">
                    <PhoneOff
                        className={cn(
                            'h-3.5 w-3.5',
                            isPhoebeDisconnect
                                ? 'text-red-700 dark:text-red-200'
                                : 'text-slate-600 dark:text-slate-300',
                        )}
                    />
                    <p
                        className={cn(
                            'text-xs font-semibold tracking-wide uppercase',
                            isPhoebeDisconnect
                                ? 'text-red-800 dark:text-red-100'
                                : 'text-slate-700 dark:text-slate-200',
                        )}
                    >
                        Call ended
                    </p>
                    {sourceBadgeLabel ? (
                        <Badge variant="outline" className="text-[10px]">
                            {sourceBadgeLabel}
                        </Badge>
                    ) : null}
                </div>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-900 dark:text-slate-50">
                    {label}
                </p>
                {attribution.disconnected_by ? (
                    <p className="text-muted-foreground mt-1 text-[11px]">
                        Twilio disconnected_by:{' '}
                        <code className="rounded bg-slate-950/5 px-1 py-0.5 font-mono text-[10px] dark:bg-slate-50/5">
                            {attribution.disconnected_by}
                        </code>
                    </p>
                ) : null}
            </Card>
            <span className="text-muted-foreground pt-2 text-right font-mono text-[10px]">
                --
            </span>
        </div>
    );
}

type LaneToggleState = Record<LaneToggleKey, boolean>;

function defaultLaneState(): LaneToggleState {
    const state: Partial<LaneToggleState> = {};
    for (const toggle of LANE_TOGGLES) {
        state[toggle.key] = toggle.defaultOn;
    }
    return state as LaneToggleState;
}

export function DeveloperTranscriptTimeline({
    entries,
    disconnectAttribution,
    className,
}: DeveloperTranscriptTimelineProps) {
    const [laneState, setLaneState] =
        useState<LaneToggleState>(defaultLaneState);

    const sortedEntries = useMemo(() => {
        return [...entries].sort((a, b) => a.sequence - b.sequence);
    }, [entries]);

    const baselineMs = useMemo(
        () => getTimelineBaselineMs(sortedEntries),
        [sortedEntries],
    );

    const laneCounts = useMemo(
        () => countEntriesByLane(sortedEntries),
        [sortedEntries],
    );

    const coalescedEntries = useMemo(() => {
        return coalesceTimelineEntries(sortedEntries);
    }, [sortedEntries]);

    const visibleEntries = useMemo(() => {
        return coalescedEntries.filter(({ entry }) => {
            const lane = classifyEntry(entry);
            if (lane === 'other') {
                return false;
            }
            return laneState[lane];
        });
    }, [coalescedEntries, laneState]);

    const disconnectAttributionEntry = disconnectAttribution
        ? renderDisconnectAttributionEntry(disconnectAttribution)
        : null;

    const hasAnyTimelineContent =
        laneCounts.conversation +
            laneCounts.tool +
            laneCounts.advisor +
            laneCounts.tts_event >
            0 || Boolean(disconnectAttributionEntry);

    if (!hasAnyTimelineContent) {
        return (
            <div
                className={cn(
                    'text-muted-foreground rounded-md border border-dashed p-4 text-sm',
                    className,
                )}
            >
                Developer timeline is not available for this call.
            </div>
        );
    }

    return (
        <div className={cn('min-w-0 space-y-2', className)}>
            <div className="flex flex-wrap items-center gap-1.5">
                {LANE_TOGGLES.map((toggle) => {
                    const count = laneCounts[toggle.key];
                    const active = laneState[toggle.key];
                    return (
                        <Button
                            key={toggle.key}
                            type="button"
                            size="sm"
                            variant={active ? 'default' : 'outline'}
                            onClick={() =>
                                setLaneState((prev) => ({
                                    ...prev,
                                    [toggle.key]: !prev[toggle.key],
                                }))
                            }
                            disabled={count === 0}
                            className="h-7 px-2 text-xs"
                        >
                            {toggle.label}
                            <span className="text-muted-foreground ml-1.5 font-normal">
                                {count}
                            </span>
                        </Button>
                    );
                })}
            </div>
            {visibleEntries.length === 0 && !disconnectAttributionEntry ? (
                <div className="text-muted-foreground rounded-md border border-dashed p-4 text-sm">
                    All lanes are hidden. Toggle a filter above to see entries.
                </div>
            ) : (
                <div className="space-y-1.5">
                    {visibleEntries.map(({ entry, count }) => {
                        const offsetSeconds = getEntryOffsetSeconds(
                            entry,
                            baselineMs,
                        );
                        const timeLabel = formatOffsetSeconds(offsetSeconds);
                        const lane = classifyEntry(entry);
                        const body =
                            lane === 'advisor'
                                ? renderAdvisorEntry(entry)
                                : lane === 'tool'
                                  ? renderToolEntry(entry)
                                  : lane === 'tts_event'
                                    ? renderTtsEventEntry(entry, count)
                                    : renderConversationEntry(entry);

                        return (
                            <div
                                key={entry.sequence}
                                className="grid min-w-0 grid-cols-[2rem_minmax(0,1fr)_2.5rem] items-start gap-1.5 text-sm sm:grid-cols-[2.5rem_minmax(0,1fr)_2.75rem]"
                            >
                                <span className="text-muted-foreground pt-2 font-mono text-xs">
                                    #{entry.sequence}
                                </span>
                                <div className="min-w-0">{body}</div>
                                <span className="text-muted-foreground pt-2 text-right font-mono text-xs">
                                    {timeLabel}
                                </span>
                            </div>
                        );
                    })}
                    {disconnectAttributionEntry}
                </div>
            )}
        </div>
    );
}
